import { RequestParser } from '@ers/helpers/api';
import { getFileWithContent, getString, validStatuses } from '@ers/helpers/api/ResponseProcessors';

class AppApi {
    constructor(api) {
        this.api = api;
    }

    fetchApplications() {
        return this.api.get(`applicationPreviews`);
    }

    fetchArchivedApplications() {
        return this.api.get(`applications/archive/previews`);
    }

    archive(applicationId, archived) {
        const body = RequestParser.toBody({ isArchived: !archived });
        return this.api.patch(`applications/${applicationId}/archive`, body);
    }

    isApplicationExists(applicationId) {
        return this.api.head(`applications/${applicationId}`);
    }

    fetchFile(applicationId, fileId) {
        return this.api.fetchFile(`applications/${applicationId}/file/${fileId}`, getFileWithContent);
    }

    fetchFileFromThread(threadId, fileId) {
        return this.api.fetchFile(`threads/${threadId}/file/${fileId}`, getFileWithContent);
    }

    confirmApplication(applicationId) {
        const isConfirmed = true;
        const body = RequestParser.toBody({ isConfirmed });
        return this.api.patch(`applications/${applicationId}`, body, ({ status }) => status, [...validStatuses, 401, 403]);
    }

    denyApplication(applicationId) {
        return this.api.delete(`applications/${applicationId}`, ({ status }) => status, [...validStatuses, 401, 403]);
    }

    fetchUserData() {
        return this.api.get(`users/current/userPreview?fields=firstname,lastname,email,city`);
    }

    fetchUserAvatar() {
        return this.api.fetchFile(`users/current/photo`, getFileWithContent);
    }

    exportUserData() {
        return this.api.fetchFile(`users/current/export`, getFileWithContent);
    }

    fetchConsents() {
        return this.api.get(`consents/`);
    }

    updatePrivacyConsent(consentId, isChecked) {
        const body = RequestParser.toBody({ consentId, isChecked });
        return this.api.patch(`consents/${consentId}`, body);
    }

    fetchNotifications() {
        return this.api.get(`notifications`);
    }

    updateNewApplicationEmailNotification(notificationId, isChecked) {
        const body = RequestParser.toBody({ notificationId, isChecked });
        return this.api.patch(`notifications/${notificationId}`, body);
    }

    deleteUserAccount() {
        return this.api.delete(`users/current`);
    }

    fetchAllThreads() {
        return this.api.get(`threadPreviews`);
    }

    fetchMessagesFromThread(threadId) {
        return this.api.get(`threads/${threadId}/messages`);
    }

    markThreadAsRead(threadId) {
        const isRead = true;
        const body = RequestParser.toBody({ isRead });
        return this.api.patch(`threads/${threadId}/`, body);
    }

    sendMessage(threadId, message) {
        const form = RequestParser.toFormData(message, message.attachments, 'attachments');
        return this.api.post(`threads/${threadId}/messages`, form);
    }

    fetchEditUrl(applicationId) {
        return this.api.get(`applications/${applicationId}/recruitmentFormEditUrl`, getString);
    }

    fetchUnreadThreadsCounter() {
        return this.api.get(`threadUnreadCounter?fields=counter`);
    }

    fetchApplicationDetails(applicationId) {
        return this.api.get(`applications/${applicationId}/form`);
    }

    fetchSingleApplication(applicationId) {
        return this.api.get(`applications/${applicationId}`);
    }

    fetchArchivedCount() {
        return this.api.get(`applications/archive/previews/count`);
    }

    sendEvent(name, data) {
        const body = RequestParser.toBody({ name, data });
        return this.api.post(`events`, body);
    }

    getJobOfferLink(applicationId) {
        return this.api.get(`applications/${applicationId}/jobofferlink`);
    }

    deleteJobOfferLink(applicationId) {
        return this.api.delete(`applications/${applicationId}/jobofferlink`);
    }

    addJobOfferLink(applicationId, url) {
        const body = RequestParser.toBody({ url });
        return this.api.post(
            `applications/${applicationId}/jobofferlink`,
            body,
            (r) => r,
            [...validStatuses, 400, 401]
        );
    }

    fetchSystemNotification() {
        return this.api.head(`popupsystemnotifications/current/display`);
    }

    updateSystemNotification() {
        return this.api.post(`popupsystemnotifications/current/display`);
    }
}

export default AppApi;